/**
 * Núcleo · IconButton — botón cuadrado solo icono
 * Catálogo §01: misma altura que Button, 0 radius, semántico.
 * sm=26px · md=32px · lg=40px
 */
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

type Size = 'sm' | 'md' | 'lg';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: Size;
  /** Marca estado activo/on (bg acento, borde acento) */
  active?: boolean;
  /** Variante destructiva */
  danger?: boolean;
  /** Tooltip text (accesibilidad) */
  label: string;
}

const dims: Record<Size, number> = { sm: 26, md: 32, lg: 40 };
const iconSizes: Record<Size, number> = { sm: 12, md: 14, lg: 16 };

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'md', active, danger, label, children, className, disabled, style, ...props }, ref) => {
    const dim = dims[size];

    const bg = active ? 'var(--nucleo-surface-hi)' : 'transparent';
    const border = active
      ? '1px solid var(--nucleo-accent)'
      : '1px solid var(--nucleo-line-mid)';
    const color = danger
      ? 'var(--nucleo-danger)'
      : active
      ? 'var(--nucleo-accent)'
      : 'var(--nucleo-ink-soft)';

    return (
      <button
        ref={ref}
        aria-label={label}
        title={label}
        disabled={disabled}
        className={clsx(
          'inline-flex items-center justify-center transition-colors',
          'hover:bg-[var(--nucleo-surface-up)]',
          'focus:outline focus:outline-2 focus:outline-[var(--nucleo-accent)] focus:outline-offset-2',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          className
        )}
        style={{ width: dim, height: dim, background: bg, border, color, ...style }}
        {...props}
      >
        {children}
      </button>
    );
  }
);
IconButton.displayName = 'IconButton';

/** Utility to get the correct icon size for a given button size */
export function iconSizeFor(size: Size = 'md') {
  return iconSizes[size];
}
